import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Wallet, AlertTriangle, Lock, CheckCircle, Building2, Smartphone } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { PageHeader, AdminCard, StatCard, PageLoader, StatusPill, Btn, ConfirmDialog, Modal, Textarea, datetimeFromIso } from "@/admin/components";
import { formatINR } from "@/lib/format";

const SYMBOLS = { GBP: "£", USD: "$", EUR: "€", INR: "₹" };
const fmtPresentment = (minor, ccy) => `${SYMBOLS[ccy] || ""}${(minor / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${ccy}`;
const PSP_LABEL = { razorpay: "Razorpay (India)", stripe_uk: "Stripe (UK Ltd)", mock: "Mock / test" };

export default function Payments() {
  const [recon, setRecon] = useState(null);
  const [preview, setPreview] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [running, setRunning] = useState(false);
  const [confirmRun, setConfirmRun] = useState(false);

  // Astrologer payouts table state
  const [astroPayouts, setAstroPayouts] = useState(null);
  const [astroPayoutsLoading, setAstroPayoutsLoading] = useState(false);

  // Confirm payout modal state
  const [payTarget, setPayTarget] = useState(null);
  const [payNote, setPayNote] = useState("");
  const [payBusy, setPayBusy] = useState(false);
  const [payStep, setPayStep] = useState("confirm"); // "confirm" | "success"
  const [payResult, setPayResult] = useState(null);
  const [payError, setPayError] = useState(null);

  const loadAll = async () => {
    const [r, p, h] = await Promise.all([
      adminApi.get("/admin/reconciliation"),
      adminApi.get("/admin/payouts/preview"),
      adminApi.get("/admin/payouts"),
    ]);
    setRecon(r.data); setPreview(p.data); setPayouts(h.data.payouts);
  };

  const loadAstroPayouts = async () => {
    setAstroPayoutsLoading(true);
    try {
      const { data } = await adminApi.get("/admin/payouts/astrologers");
      setAstroPayouts(data.astrologers);
    } catch { toast.error("Couldn't load astrologer payouts"); }
    finally { setAstroPayoutsLoading(false); }
  };

  useEffect(() => {
    loadAll().catch(() => toast.error("Couldn't load payments data"));
    loadAstroPayouts();
  }, []);

  const runPayouts = async () => {
    setConfirmRun(false); setRunning(true);
    try {
      const { data } = await adminApi.post("/admin/payouts/run");
      toast.success(data.run_id ? `Paid ${formatINR(data.total_paid_inr || 0)} across ${data.payouts.filter((x) => x.status === "paid").length} astrologer(s)` : "No eligible payouts");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payout run failed");
    } finally { setRunning(false); }
  };

  const downloadStatement = async (aid, name) => {
    try {
      const res = await adminApi.get(`/admin/astrologers/${aid}/payout-statement`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `payout_statement_${(name || aid).replace(/\s+/g, "_")}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Couldn't download statement"); }
  };

  const openPayModal = (row) => {
    setPayTarget(row); setPayNote(""); setPayStep("confirm");
    setPayResult(null); setPayError(null); setPayBusy(false);
  };

  const closePayModal = () => {
    if (payStep === "success") loadAstroPayouts();
    setPayTarget(null); setPayNote(""); setPayStep("confirm");
    setPayResult(null); setPayError(null);
  };

  const doConfirmPayout = async () => {
    setPayBusy(true); setPayError(null);
    try {
      const { data } = await adminApi.post(`/admin/payouts/astrologer/${payTarget.astrologer_id}`, { note: payNote });
      setPayResult(data); setPayStep("success");
    } catch (e) {
      setPayError(e?.response?.data?.detail || "Payout failed. Please try again.");
    } finally { setPayBusy(false); }
  };

  if (!recon || !preview || !payouts) return <PageLoader />;

  const inflowPayable = recon.summary.reduce((s, x) => s + (x.astrologer_payable_inr || 0), 0);

  return (
    <div data-testid="admin-payments-page">
      <PageHeader title="Payments & Payouts" subtitle="Multi-currency inflows, reconciliation and astrologer payouts" />

      {/* Top stats */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard testid="pay-stat-ledger" label="Ledger transactions" value={recon.ledger_count} />
        <StatCard testid="pay-stat-payable" label="Total astrologer payable" value={formatINR(inflowPayable / 100)} />
        <StatCard testid="pay-stat-pending" label="Pending payout (eligible)" value={formatINR(preview.total_inr)} hint={`${preview.eligible.length} astrologer(s) · ${preview.reserve_days}d reserve`} />
        <StatCard testid="pay-stat-abandoned" label="Abandoned card checkouts" value={recon.abandoned_checkouts} />
      </div>

      {/* Inflows by currency / PSP / entity */}
      <AdminCard className="mt-6 p-6" testid="inflows-table">
        <h3 className="mb-4 text-[15px] font-semibold text-[#111]">Inflows by currency &amp; payment provider</h3>
        {recon.summary.length === 0 ? (
          <p className="text-[14px] text-[#999]">No ledger transactions yet. They appear here once consultations are paid.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left text-[14px]">
              <thead>
                <tr className="border-b border-[#e9e6e1] text-[11px] uppercase tracking-[0.06em] text-[#999]">
                  <th className="px-3 py-2.5 font-semibold">Provider</th>
                  <th className="px-3 py-2.5 font-semibold">Currency</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Count</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Inflow collected</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Astrologer payable (₹)</th>
                </tr>
              </thead>
              <tbody>
                {recon.summary.map((s, i) => (
                  <tr key={i} className="border-b border-[#f2efea] text-[#222]">
                    <td className="px-3 py-3 font-medium">{PSP_LABEL[s.psp] || s.psp}</td>
                    <td className="px-3 py-3">{s.currency}</td>
                    <td className="px-3 py-3 text-right">{s.count}</td>
                    <td className="px-3 py-3 text-right font-medium text-[#111]">{fmtPresentment(s.presentment_minor, s.currency)}</td>
                    <td className="px-3 py-3 text-right text-[#666]">{formatINR(s.astrologer_payable_inr / 100)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {recon.flags.length > 0 && (
          <div className="mt-4 flex items-start gap-2 rounded-[12px] border border-[#f0d9a8] bg-[#fdf6e6] p-3" data-testid="recon-flags">
            <AlertTriangle size={16} className="mt-0.5 shrink-0 text-[#b8860b]" />
            <div className="text-[13px] text-[#7a5c12]">
              <span className="font-semibold">{recon.flags.length} reconciliation flag(s):</span>{" "}
              {recon.flags.slice(0, 5).map((f) => f.type).join(", ")}{recon.flags.length > 5 ? "…" : ""}
            </div>
          </div>
        )}
      </AdminCard>

      {/* Astrologer Payouts table */}
      <AdminCard className="mt-6 p-6" testid="astrologer-payouts-card">
        <div className="mb-4">
          <h3 className="text-[15px] font-semibold text-[#111]">Astrologer Payouts</h3>
          <p className="mt-0.5 text-[13px] text-[#999]">Per-astrologer earnings, fees and payout status</p>
        </div>
        {astroPayoutsLoading ? (
          <div className="py-8 text-center text-[13px] text-[#999]">Loading…</div>
        ) : !astroPayouts || astroPayouts.length === 0 ? (
          <p className="py-6 text-center text-[13px] text-[#999]">No payable astrologers yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left text-[14px]" data-testid="astro-payouts-table">
              <thead>
                <tr className="border-b border-[#e9e6e1] text-[11px] uppercase tracking-[0.06em] text-[#999]">
                  <th className="px-3 py-2.5 font-semibold">Astrologer</th>
                  <th className="px-3 py-2.5 font-semibold">Bank / UPI</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Gross Earned</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Platform Fee (5%)</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Razorpay Fee (2%)</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Net Payable</th>
                  <th className="px-3 py-2.5 font-semibold">Status</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {astroPayouts.map((r) => {
                  const initials = (r.astrologer_name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
                  const bankDisplay = r.payout_method === "upi" ? r.upi_id : r.account_number || "—";
                  return (
                    <tr key={r.astrologer_id} className="border-b border-[#f2efea] text-[#222]" data-testid={`astro-payout-row-${r.astrologer_id}`}>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f2f0ec] text-[11px] font-bold text-[#555]">{initials}</div>
                          <span className="font-medium">{r.astrologer_name}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 font-mono text-[12px] text-[#666]">{bankDisplay || "—"}</td>
                      <td className="px-3 py-3 text-right text-[#555]">{formatINR(r.gross_earned_inr)}</td>
                      <td className="px-3 py-3 text-right text-[#c0392b]">-{formatINR(r.platform_fee_inr)}</td>
                      <td className="px-3 py-3 text-right text-[#c0392b]">-{formatINR(r.razorpay_fee_inr)}</td>
                      <td className="px-3 py-3 text-right font-semibold text-[#111]">{formatINR(r.net_payable_inr)}</td>
                      <td className="px-3 py-3">
                        {r.status === "eligible" && (
                          <span className="rounded-full bg-[#e8f5e9] px-2.5 py-1 text-[11px] font-semibold text-[#2e7d32]">Eligible</span>
                        )}
                        {r.status === "on_hold" && (
                          <span className="rounded-full bg-[#fdf3e3] px-2.5 py-1 text-[11px] font-semibold text-[#a16207]">On Hold · {r.hold_days_remaining}d left</span>
                        )}
                        {r.status === "paid" && (
                          <span className="rounded-full bg-[#f2f0ec] px-2.5 py-1 text-[11px] font-semibold text-[#888]">Paid</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right">
                        <button
                          data-testid={`pay-now-${r.astrologer_id}`}
                          onClick={() => openPayModal(r)}
                          disabled={!r.payout_eligible}
                          className={`h-8 rounded-[10px] px-3 text-[12px] font-semibold transition-colors ${r.payout_eligible ? "bg-[#111] text-white hover:bg-[#333]" : "cursor-not-allowed bg-[#f2f0ec] text-[#bbb]"}`}
                        >
                          Pay Now
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </AdminCard>

      {/* Run payouts */}
      <AdminCard className="mt-6 p-6" testid="run-payouts-card">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-[#111]">
              <Wallet size={16} />
              <h3 className="text-[15px] font-semibold">Run astrologer payouts</h3>
              {!preview.live && <span className="rounded-full bg-[#f2f0ec] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#888]" data-testid="payout-mock-badge">Mock disbursement</span>}
            </div>
            <p className="mt-1 text-[13px] text-[#777]">
              <span className="font-semibold text-[#111]">{formatINR(preview.total_inr)}</span> across{" "}
              <span className="font-semibold text-[#111]">{preview.eligible.length}</span> astrologer(s), aggregated from INR + foreign-currency
              consultations older than the {preview.reserve_days}-day reserve.
            </p>
          </div>
          <Btn testid="run-payouts-btn" onClick={() => setConfirmRun(true)} busy={running} disabled={preview.eligible.length === 0}>
            Run payouts
          </Btn>
        </div>
      </AdminCard>

      {/* Payout history */}
      <AdminCard className="mt-6 p-6" testid="payout-history">
        <h3 className="mb-4 text-[15px] font-semibold text-[#111]">Payout history</h3>
        {payouts.length === 0 ? (
          <p className="text-[14px] text-[#999]">No payouts yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left text-[14px]">
              <thead>
                <tr className="border-b border-[#e9e6e1] text-[11px] uppercase tracking-[0.06em] text-[#999]">
                  <th className="px-3 py-2.5 font-semibold">Date</th>
                  <th className="px-3 py-2.5 font-semibold">Astrologer</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Amount</th>
                  <th className="px-3 py-2.5 font-semibold">Method</th>
                  <th className="px-3 py-2.5 font-semibold">Status</th>
                  <th className="px-3 py-2.5 font-semibold">Payout ID</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Statement</th>
                </tr>
              </thead>
              <tbody>
                {payouts.map((p) => (
                  <tr key={p.id} className="border-b border-[#f2efea] text-[#222]" data-testid={`payout-row-${p.id}`}>
                    <td className="px-3 py-3 text-[#666]">{datetimeFromIso(p.created_at)}</td>
                    <td className="px-3 py-3 font-medium">{p.astrologer_name || p.astrologer_id}</td>
                    <td className="px-3 py-3 text-right font-semibold text-[#111]">{formatINR(p.total_payable_inr)}</td>
                    <td className="px-3 py-3 text-[#666]">{p.payout_method ? `${p.payout_method.toUpperCase()} · ${p.payout_destination}` : "—"}</td>
                    <td className="px-3 py-3"><StatusPill status={p.status} /></td>
                    <td className="px-3 py-3 font-mono text-[12px] text-[#888]">{p.razorpayx_payout_id || "—"}</td>
                    <td className="px-3 py-3 text-right">
                      <button data-testid={`statement-${p.astrologer_id}`} onClick={() => downloadStatement(p.astrologer_id, p.astrologer_name)}
                        className="inline-flex items-center gap-1.5 rounded-[9px] border border-[#e0ddd7] px-2.5 py-1.5 text-[12px] font-medium text-[#555] transition-colors hover:border-[#111] hover:text-[#111]">
                        <Download size={13} /> CSV
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminCard>

      <ConfirmDialog
        open={confirmRun}
        onClose={() => setConfirmRun(false)}
        onConfirm={runPayouts}
        danger={false}
        title="Run astrologer payouts?"
        confirmLabel="Run payouts"
        busy={running}
        message={`This will disburse ${formatINR(preview.total_inr)} to ${preview.eligible.length} astrologer(s)${preview.live ? "" : " (MOCK — RazorpayX not yet live)"}. Eligible consultations past the ${preview.reserve_days}-day reserve will be marked paid.`}
      />

      {/* Confirm Payout Modal */}
      <Modal open={!!payTarget} onClose={closePayModal} title="Confirm Payout" testid="confirm-payout-modal">
        {payTarget && payStep === "confirm" && (
          <div>
            <p className="mb-5 text-[13px] text-[#999]">Review the details carefully before confirming.</p>

            {/* Astrologer avatar + name */}
            <div className="mb-5 flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#f2f0ec] text-[13px] font-bold text-[#555]">
                {(payTarget.astrologer_name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
              </div>
              <div>
                <p className="font-semibold text-[#111]">{payTarget.astrologer_name}</p>
                <p className="text-[12px] text-[#999]">Astrologer</p>
              </div>
            </div>

            {/* Paying to */}
            <div className="mb-5 rounded-[12px] border border-[#e9e6e1] p-4">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#999]">Paying to</p>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {payTarget.payout_method === "upi"
                    ? <Smartphone size={14} className="text-[#555]" />
                    : <Building2 size={14} className="text-[#555]" />}
                  <span className="font-mono text-[13px] font-medium text-[#111]">
                    {payTarget.payout_method === "upi" ? payTarget.upi_id : payTarget.account_number}
                  </span>
                </div>
                {payTarget.kyc_complete && (
                  <span className="flex items-center gap-1 rounded-full bg-[#e8f5e9] px-2 py-0.5 text-[10px] font-semibold text-[#2e7d32]">
                    <CheckCircle size={10} /> VERIFIED
                  </span>
                )}
              </div>
            </div>

            {/* Fee breakdown */}
            <div className="mb-5 rounded-[12px] bg-[#fafaf9] p-4">
              <div className="space-y-2 text-[13px]">
                <div className="flex justify-between text-[#555]">
                  <span>Gross earned</span>
                  <span>{formatINR(payTarget.gross_earned_inr)}</span>
                </div>
                <div className="flex justify-between text-[#c0392b]">
                  <span>Platform fee (5%)</span>
                  <span>-{formatINR(payTarget.platform_fee_inr)}</span>
                </div>
                <div className="flex justify-between text-[#c0392b]">
                  <span>Razorpay fee (2%)</span>
                  <span>-{formatINR(payTarget.razorpay_fee_inr)}</span>
                </div>
                <div className="my-2 border-t border-[#e9e6e1]" />
                <div className="flex justify-between">
                  <span className="text-[15px] font-bold text-[#111]">NET PAYOUT</span>
                  <span className="text-[15px] font-bold text-[#111]">{formatINR(payTarget.net_payable_inr)}</span>
                </div>
                <div className="flex items-center gap-1.5 pt-1 text-[#666]">
                  {payTarget.payout_method === "upi"
                    ? <><Smartphone size={12} /> UPI</>
                    : <><Building2 size={12} /> Bank Transfer</>}
                </div>
              </div>
            </div>

            {/* Internal note */}
            <div className="mb-5">
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.06em] text-[#999]">Internal Note</label>
              <Textarea
                data-testid="payout-note"
                value={payNote}
                onChange={(e) => setPayNote(e.target.value)}
                placeholder="Internal note (optional)"
                rows={2}
              />
            </div>

            {payError && (
              <div className="mb-4 rounded-[10px] border border-[#fca5a5] bg-[#fef2f2] px-4 py-3 text-[13px] text-[#c0392b]" data-testid="payout-error">
                {payError}
              </div>
            )}

            <div className="flex items-center gap-3">
              <button
                data-testid="payout-cancel"
                onClick={closePayModal}
                className="flex-1 rounded-[12px] border border-[#e5e5e5] bg-white py-2.5 text-[14px] font-medium text-[#666] hover:border-[#999]"
              >
                Cancel
              </button>
              <button
                data-testid="payout-confirm"
                onClick={doConfirmPayout}
                disabled={payBusy}
                className="flex flex-1 items-center justify-center gap-2 rounded-[12px] bg-[#111] py-2.5 text-[14px] font-semibold text-white disabled:opacity-60 hover:bg-[#333]"
              >
                {payBusy && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />}
                Confirm Payout {formatINR(payTarget.net_payable_inr)} →
              </button>
            </div>

            <div className="mt-4 flex items-center justify-center gap-1.5 text-[11px] text-[#bbb]">
              <Lock size={10} /> Secure processing via RazorpayX
            </div>
          </div>
        )}

        {payTarget && payStep === "success" && payResult && (
          <div>
            <div className="mb-5 flex flex-col items-center gap-2 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#e8f5e9]">
                <CheckCircle size={28} className="text-[#2e7d32]" />
              </div>
              <p className="text-[16px] font-semibold text-[#111]">Payout initiated successfully</p>
              <p className="text-[13px] text-[#999]">Request sent to provider</p>
            </div>

            <div className="mb-5 rounded-[12px] border border-[#e9e6e1] divide-y divide-[#f2efea] text-[13px]">
              {[
                ["Reference", payResult.run_id],
                ["Astrologer", payResult.astrologer_name],
                ["Amount", formatINR(payResult.net_payable_inr)],
                ["Paid to", payResult.payout_destination],
                ["Date", new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })],
              ].map(([label, value]) => (
                <div key={label} className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-[#999]">{label}</span>
                  <span className="font-medium text-[#111]">{value}</span>
                </div>
              ))}
              <div className="flex items-center justify-between px-4 py-2.5">
                <span className="text-[#999]">Status</span>
                <span className="flex items-center gap-1.5 font-medium text-[#a16207]">
                  <span className="h-2 w-2 rounded-full bg-[#f59e0b]" /> Processing
                </span>
              </div>
            </div>

            <p className="mb-5 text-center text-[12px] text-[#bbb]">RazorpayX takes a few minutes to complete the transfer.</p>

            <button
              data-testid="payout-done"
              onClick={closePayModal}
              className="w-full rounded-[12px] bg-[#111] py-3 text-[14px] font-semibold text-white hover:bg-[#333]"
            >
              Done
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
}
